import { DateTime } from '@ksyos/date-time';
import { Alert, Text } from '@ksyos/design-system';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { ManagerAuthorization } from '@common/enums';
import { isActiveOnDate } from '@common/utils/isActiveOnDate';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import {
    assertHasAuthorization,
    scrollToFormControlFeedback,
    scrollToValidationErrorByClassName,
} from '@frontend/utils';
import type { ValidationErrors } from '../../../utils/validation';
import type { IBreadcrumbItem } from '../../elements/common';
import type { IGroupSetting } from '../../interfaces/groupSetting';
import type { IOrganization } from '../../interfaces/organization';
import type { IService } from '../../interfaces/service';
import type { IServiceConsumer } from '../../interfaces/serviceConsumer';
import type { IServiceProvider } from '../../interfaces/serviceProvider';
import type {
    IServiceOrganizationGroupHistory,
    IServiceOrganizationGroupHistoryPractitioner,
} from './ServiceOrganizationGroupHistorySelector';
import { BittiumRecordingAnalyst } from '../../../../common/enums/bittiumRecordingAnalyst';
import { ServiceCode } from '../../../../common/enums/serviceCode';
import { ServiceType } from '../../../../common/enums/serviceType';
import { SomnologyOrganizationType } from '../../../../common/enums/somnologyOrganizationType';
import { getAnalystAllowedByServiceCode } from '../../../../common/utils/getAnalystAllowedByServiceCode';
import { serviceOrganizationSettingAddEditSchema } from '../../../../common/validation/serviceOrganizationSetting';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { OverlapChecker } from '../../../utils/overlapChecker';
import { RateLimiter } from '../../../utils/rateLimiter';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import {
    Button,
    CancelButton,
    DateInput,
    Dropdown,
    EnumDropdown,
    FormContainer,
    FormDivider,
    FormGroup,
    FormHeader,
    FormLabel,
    FormMessage,
    Header,
    InputCheckbox,
    InputGroup,
    InputRadioGroup,
    Loading,
    NullableInputText,
    OverlapMessage,
    SaveSubmit,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { OrganizationSelection } from '../../elements/OrganizationSelection';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';
import { CompensationPayoutList } from '../compensationPayouts/CompensationPayoutList';
import { FundingSettingsList } from '../fundingSettings/FundingSettingsList';
import {
    ServiceOrganizationGroupHistorySelector,
    validateServiceOrganizationGroupHistorySelector,
} from './ServiceOrganizationGroupHistorySelector';

type IServiceOrganizationSettingsAddEditProps = IWithRouterProps;

interface IServiceGraderPool {
    id: string;
    name: string;
}

interface IServiceOrganizationSettings {
    canReferPatientWithoutSmartphone: boolean;
    emailAddressOrganizationInbox: string | null;
    emailAddressOrganizationInboxAlt: string | null;
    endDate: string | null;
    id?: string;
    isRequestRecipient: boolean;
    isSendingDeviceToPatientsAddress: boolean;
    isUsingHideRequestedExaminationTaskFeature: boolean;
    ksyosHolterAnalystOrganizationId: string | null;
    optoConnectPracticeId: string | null;
    organizationId: string | null;
    receiveZorgmailOnConsultOpen: boolean;
    recipientIsOwnOrganization: boolean;
    sendBittiumRecordingsTo: BittiumRecordingAnalyst | null;
    sendNotificationOrganizationInbox: boolean;
    serviceGraderPoolId: string | null;
    serviceGroupSettingId: string | null;
    serviceId: string | null;
    serviceOrganizationGroupHistory: IServiceOrganizationGroupHistory[];
    somnologyOrganizationType: SomnologyOrganizationType | null;
    startDate: string | null;
    zorgmailNummer: string | null;
}

interface IServiceOrganizationSettingsAddEditState {
    errors: ValidationErrors;
    hasTcohBiometricianInOrganization: boolean;

    isLoading: boolean;
    isSaving: boolean;
    messageNoServiceProviderRecipient?: boolean;
    organizationEmail: string | null;
    organizationName: string | null;
    organizations: IOrganization[];
    overlap?: {
        endDate: string | null;
        id: string;
        startDate: string;
    };
    serviceCode: ServiceCode | null;
    serviceConsumers: IServiceConsumer[];
    serviceGraderPools: IServiceGraderPool[];
    serviceGroupSettings: IGroupSetting[];
    serviceName: string | null;
    serviceOrganizationSettings?: IServiceOrganizationSettings;
    serviceProviders: IServiceProvider[];
    services: IService[];
    serviceType: ServiceType | null;
    useAnalyst: boolean;
}

class InnerServiceOrganizationSettingsAddEdit extends Component<
    IServiceOrganizationSettingsAddEditProps,
    IServiceOrganizationSettingsAddEditState
> {
    private atMostOncePerSecond = new RateLimiter(1000);
    private overlapChecker = new OverlapChecker('/api/serviceOrganizationSettings');

    constructor(props: IServiceOrganizationSettingsAddEditProps, context?: {}) {
        super(props, context);
        assertHasAuthorization(ManagerAuthorization.serviceRightsOrganizations);

        const serviceId = this.props.router.searchParams.get('serviceId');
        const organizationId = this.props.router.searchParams.get('organizationId');
        if (!this.props.router.params.id && !serviceId && !organizationId) {
            throw new Error('Expected id, serviceId, or organizationId');
        } else if (serviceId && organizationId) {
            throw new Error('Cannot have both serviceId and organizationId');
        }

        this.state = {
            services: [],
            organizations: [],
            serviceGraderPools: [],
            isSaving: false,
            errors: emptyValidationErrors,
            serviceType: null,
            serviceName: null,
            serviceCode: null,
            organizationName: null,
            organizationEmail: null,
            isLoading: false,
            useAnalyst: false,
            serviceGroupSettings: [],
            hasTcohBiometricianInOrganization: false,
            serviceConsumers: [],
            serviceProviders: [],
        };
    }

    public async componentDidMount() {
        await this.load();
        await this.checkServiceProviderIsRequestRecipient();
        if (this.state.serviceCode === ServiceCode.TCOH) {
            await this.hasTcohBiometricianInOrganization();
        }
    }

    public render() {
        const breadcrumbs: IBreadcrumbItem[] = [];
        const serviceId = this.props.router.searchParams.getOptional('serviceId');
        const organizationId = this.props.router.searchParams.getOptional('organizationId');
        if (this.state.serviceName) {
            breadcrumbs.push({ text: this.state.serviceName, url: `/services/${serviceId}` });
        } else if (this.state.organizationName) {
            breadcrumbs.push({
                text: this.state.organizationName,
                url: `/organizations/${organizationId}`,
            });
        }

        if (this.props.router.params.id) {
            breadcrumbs.push({ text: translateKey('common.edit') });
        } else {
            breadcrumbs.push({ text: translateKey('common.new') });
        }

        breadcrumbs.push({ text: translateKey('serviceProvider.serviceOrganizationSettings'), url: this.returnTo() });

        const header = `${
            this.props.router.params.id ? translateKey('common.edit') : translateKey('common.add')
        } ${translateKey('serviceProvider.serviceOrganizationSettings')}`;

        return (
            <FixedWidthTemplate breadcrumb={breadcrumbs}>
                <Header content={header} />
                <Loading
                    item={this.state.serviceOrganizationSettings}
                    loaded={(serviceOrganizationSettings) => (
                        <FormContainer
                            onSubmit={async () => {
                                const serviceOrganizationSettingsId = await this.save();
                                if (serviceOrganizationSettingsId) {
                                    this.props.router.navigate(this.returnTo());
                                }
                            }}
                        >
                            {this.state.overlap && (
                                <OverlapMessage
                                    endDate={this.state.overlap.endDate}
                                    link={`/serviceOrganizationSettings/${this.state.overlap.id}?${
                                        serviceId ? `serviceId=${serviceId}` : `organizationId=${organizationId}`
                                    }`}
                                    message={translateKey('serviceProvider.overlapMessage')}
                                    startDate={this.state.overlap.startDate}
                                />
                            )}
                            {serviceId && !this.props.router.params.id && (
                                <FormGroup hasErrors={this.state.errors.has('organizationId')}>
                                    <FormLabel isRequired>{translateKey('organization.organization')}</FormLabel>
                                    <InputGroup>
                                        <OrganizationSelection
                                            error={this.state.errors.get('organizationId')}
                                            onChange={async (changedOrganizationId) => {
                                                await this.handleChange(
                                                    'organizationId',
                                                    changedOrganizationId || null
                                                );
                                                await this.reloadOrganization(changedOrganizationId || null);
                                            }}
                                            onDoValidate={async () => await this.validateOne('organizationId')}
                                            showCity
                                            value={serviceOrganizationSettings.organizationId ?? undefined}
                                        />
                                    </InputGroup>
                                </FormGroup>
                            )}
                            {organizationId && !this.props.router.params.id && (
                                <FormGroup hasErrors={this.state.errors.has('serviceId')}>
                                    <FormLabel isRequired>{translateKey('service.service')}</FormLabel>
                                    <InputGroup>
                                        <Dropdown
                                            error={this.state.errors.get('serviceId')}
                                            onChange={async (servId) => {
                                                await this.handleChange('serviceId', servId);
                                                await this.checkServiceProviderIsRequestRecipient();
                                                await this.reloadServiceGraderPools(servId);
                                            }}
                                            onDoValidate={async () => await this.validateOne('serviceId')}
                                            selectOptions={this.getServicesOptions()}
                                            value={serviceOrganizationSettings.serviceId}
                                        />
                                    </InputGroup>
                                </FormGroup>
                            )}
                            <FormGroup hasErrors={this.state.errors.has('startDate')}>
                                <FormLabel isRequired>{translateKey('common.startDate')}</FormLabel>
                                <InputGroup>
                                    <DateInput
                                        error={this.state.errors.get('startDate')}
                                        onChange={async (value) => await this.handleChange('startDate', value)}
                                        onDoValidate={async () => await this.validateOne('startDate')}
                                        value={serviceOrganizationSettings.startDate}
                                    />
                                </InputGroup>
                            </FormGroup>
                            <FormGroup hasErrors={this.state.errors.has('endDate')}>
                                <FormLabel>{translateKey('common.endDate')}</FormLabel>
                                <InputGroup>
                                    <DateInput
                                        error={this.state.errors.get('endDate')}
                                        onChange={async (value) => await this.handleChange('endDate', value)}
                                        value={serviceOrganizationSettings.endDate}
                                    />
                                </InputGroup>
                            </FormGroup>
                            <FormHeader>{translateKey('setting.settings')}</FormHeader>
                            {this.state.messageNoServiceProviderRecipient && (
                                <Alert appearance="info">
                                    {translateKey('serviceProvider.messageNoServiceProviderRecipient')}
                                </Alert>
                            )}

                            {this.props.router.params.id && (
                                <FormGroup hasErrors={this.state.errors.has('serviceOrganizationGroupHistory')}>
                                    <FormLabel>{translateKey('serviceOrganizationSetting.currentCareGroup')}</FormLabel>
                                    <InputGroup stacked={true}>
                                        <Text py={1}>{this.getActiveGroupName()}</Text>
                                    </InputGroup>

                                    <ServiceOrganizationGroupHistorySelector
                                        errorKey="serviceOrganizationGroupHistory"
                                        errors={this.state.errors}
                                        onChange={async (value) => {
                                            await this.handleChange('serviceOrganizationGroupHistory', value);
                                        }}
                                        onDoValidate={async (value) => {
                                            await this.serviceOrganizationGroupHistorySelectorValidation(value);
                                        }}
                                        serviceId={this.state.serviceOrganizationSettings?.serviceId ?? undefined}
                                        serviceOrganizationGroupHistory={
                                            this.state.serviceOrganizationSettings?.serviceOrganizationGroupHistory ??
                                            []
                                        }
                                        serviceOrganizationSettingId={this.props.router.params.id}
                                    />
                                </FormGroup>
                            )}

                            <FormGroup hasErrors={this.state.errors.has('isRequestRecipient')}>
                                <FormLabel>{translateKey('serviceProvider.isRequestRecipient')}</FormLabel>
                                <InputGroup>
                                    <InputCheckbox
                                        checked={serviceOrganizationSettings.isRequestRecipient}
                                        error={this.state.errors.get('isRequestRecipient')}
                                        onChange={async (isRequestRecipient) => {
                                            await this.handleChange('isRequestRecipient', isRequestRecipient);
                                            await this.checkServiceProviderIsRequestRecipient();
                                        }}
                                    />
                                </InputGroup>
                            </FormGroup>
                            {this.state.serviceCode === ServiceCode.TCOH &&
                                this.state.hasTcohBiometricianInOrganization && (
                                    <FormGroup hasErrors={this.state.errors.has('isSendingDeviceToPatientsAddress')}>
                                        <FormLabel>
                                            {translateKey(
                                                'serviceOrganizationSetting.isSendingDeviceToPatientsAddress'
                                            )}
                                        </FormLabel>
                                        <InputGroup>
                                            <InputCheckbox
                                                checked={!!serviceOrganizationSettings.isSendingDeviceToPatientsAddress}
                                                error={this.state.errors.get('isSendingDeviceToPatientsAddress')}
                                                onChange={async (isSendingDeviceToPatientsAddress) =>
                                                    await this.handleChange(
                                                        'isSendingDeviceToPatientsAddress',
                                                        isSendingDeviceToPatientsAddress
                                                    )
                                                }
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                )}
                            {this.state.serviceCode === ServiceCode.OSASV &&
                                this.state.serviceConsumers.length > 0 &&
                                this.state.serviceProviders.length > 0 && (
                                    <FormGroup hasErrors={this.state.errors.has('recipientIsOwnOrganization')}>
                                        <FormLabel>
                                            {translateKey('addCarePathSettingsLabelsServiceConsumer.referralToSelf')}
                                        </FormLabel>
                                        <InputGroup>
                                            <InputCheckbox
                                                checked={serviceOrganizationSettings.recipientIsOwnOrganization}
                                                error={this.state.errors.get('recipientIsOwnOrganization')}
                                                onChange={async (recipientIsOwnOrganization) => {
                                                    await this.handleChange(
                                                        'recipientIsOwnOrganization',
                                                        recipientIsOwnOrganization
                                                    );
                                                    await this.checkServiceProviderIsRequestRecipient();
                                                }}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                )}
                            {this.state.serviceType === ServiceType.consultation && (
                                <>
                                    <FormGroup hasErrors={this.state.errors.has('receiveZorgmailOnConsultOpen')}>
                                        <FormLabel>
                                            {translateKey('serviceProvider.receiveZorgmailOnConsultOpen')}
                                        </FormLabel>
                                        <InputGroup>
                                            <InputCheckbox
                                                checked={serviceOrganizationSettings.receiveZorgmailOnConsultOpen}
                                                error={this.state.errors.get('receiveZorgmailOnConsultOpen')}
                                                onChange={async (value) =>
                                                    await this.handleChange('receiveZorgmailOnConsultOpen', value)
                                                }
                                                onDoValidate={async () => {
                                                    await this.validateOne('receiveZorgmailOnConsultOpen');
                                                    if (this.state.errors.has('zorgmailNummer')) {
                                                        await this.validateOne('zorgmailNummer');
                                                    }
                                                }}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                    <FormGroup hasErrors={this.state.errors.has('zorgmailNummer')}>
                                        <FormLabel
                                            isRequired={serviceOrganizationSettings.receiveZorgmailOnConsultOpen}
                                        >
                                            {translateKey('employment.zorgmailNummer')}
                                        </FormLabel>
                                        <InputGroup>
                                            <NullableInputText
                                                error={this.state.errors.get('zorgmailNummer')}
                                                onChange={async (value) =>
                                                    await this.handleChange('zorgmailNummer', value)
                                                }
                                                onDoValidate={async () => await this.validateOne('zorgmailNummer')}
                                                placeholder={translateKey('employment.zorgmailNummer')}
                                                value={serviceOrganizationSettings.zorgmailNummer}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                </>
                            )}
                            {this.state.serviceCode &&
                                [ServiceCode.OSASV, ServiceCode.OSASO, ServiceCode.OSASVO].includes(
                                    this.state.serviceCode
                                ) && (
                                    <FormGroup hasErrors={this.state.errors.has('somnologyOrganizationType')}>
                                        <FormLabel>
                                            {translateKey('serviceProvider.somnologyOrganizationType')}
                                        </FormLabel>
                                        <InputGroup>
                                            <EnumDropdown
                                                enumName="somnologyOrganizationType"
                                                error={this.state.errors.get('somnologyOrganizationType')}
                                                jsEnum={SomnologyOrganizationType}
                                                onChange={async (value) =>
                                                    await this.handleChange('somnologyOrganizationType', value)
                                                }
                                                onDoValidate={async () =>
                                                    await this.validateOne('somnologyOrganizationType')
                                                }
                                                placeholder={translateKey('serviceProvider.somnologyOrganizationType')}
                                                value={serviceOrganizationSettings.somnologyOrganizationType}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                )}
                            {this.state.serviceCode === ServiceCode.OSASV && (
                                <FormGroup hasErrors={this.state.errors.has('recipientIsOwnOrganization')}>
                                    <FormLabel>
                                        {translateKey('addCarePathSettingsLabelsServiceConsumer.referralToSelf')}
                                    </FormLabel>
                                    <InputGroup>
                                        <InputCheckbox
                                            checked={serviceOrganizationSettings.recipientIsOwnOrganization}
                                            error={this.state.errors.get('recipientIsOwnOrganization')}
                                            onChange={async (value) =>
                                                await this.handleChange('recipientIsOwnOrganization', value)
                                            }
                                            onDoValidate={async () =>
                                                await this.validateOne('recipientIsOwnOrganization')
                                            }
                                        />
                                    </InputGroup>
                                </FormGroup>
                            )}
                            {this.state.serviceCode === ServiceCode.OSASV && (
                                <FormGroup hasErrors={this.state.errors.has('canReferPatientWithoutSmartphone')}>
                                    <FormLabel>Slaap device type</FormLabel>
                                    <InputGroup>
                                        <InputRadioGroup
                                            labels={['Alleen NightOwl', 'Nightowl en WatchPAT']}
                                            name="canReferPatientWithoutSmartphone"
                                            onChange={async (value) =>
                                                await this.handleChange(
                                                    'canReferPatientWithoutSmartphone',
                                                    value === 'true'
                                                )
                                            }
                                            options={['false', 'true']}
                                            value={
                                                serviceOrganizationSettings.canReferPatientWithoutSmartphone
                                                    ? 'true'
                                                    : 'false'
                                            }
                                        />
                                    </InputGroup>
                                </FormGroup>
                            )}
                            {this.state.serviceCode === ServiceCode.TCOH && (
                                <FormGroup hasErrors={this.state.errors.has('sendBittiumRecordingsTo')}>
                                    <FormLabel>{translateKey('serviceProvider.sendBittiumRecordingsTo')}</FormLabel>
                                    <InputGroup>
                                        <EnumDropdown
                                            enumName="bittiumRecordingAnalyst"
                                            error={this.state.errors.get('sendBittiumRecordingsTo')}
                                            jsEnum={BittiumRecordingAnalyst}
                                            onChange={async (value) =>
                                                await this.handleChange('sendBittiumRecordingsTo', value)
                                            }
                                            onDoValidate={async () => await this.validateOne('sendBittiumRecordingsTo')}
                                            placeholder={translateKey('serviceProvider.sendBittiumRecordingsTo')}
                                            value={serviceOrganizationSettings.sendBittiumRecordingsTo}
                                        />
                                    </InputGroup>
                                </FormGroup>
                            )}
                            {this.state.serviceCode === ServiceCode.TOO && (
                                <FormGroup hasErrors={this.state.errors.has('optoConnectPracticeId')}>
                                    <FormLabel>{translateKey('serviceProvider.optoConnectPracticeId')}</FormLabel>
                                    <InputGroup>
                                        <NullableInputText
                                            error={this.state.errors.get('optoConnectPracticeId')}
                                            onChange={async (value) =>
                                                await this.handleChange('optoConnectPracticeId', value)
                                            }
                                            onDoValidate={async () => await this.validateOne('optoConnectPracticeId')}
                                            placeholder={translateKey('serviceProvider.optoConnectPracticeId')}
                                            value={serviceOrganizationSettings.optoConnectPracticeId}
                                        />
                                    </InputGroup>
                                </FormGroup>
                            )}
                            <FormHeader>{translateKey('common.notifications')}</FormHeader>
                            <FormGroup hasErrors={this.state.errors.has('sendNotificationOrganizationInbox')}>
                                <FormLabel>
                                    {translateKey('serviceProvider.sendNotificationOrganizationInbox')}
                                </FormLabel>
                                <InputGroup>
                                    <InputCheckbox
                                        checked={serviceOrganizationSettings.sendNotificationOrganizationInbox}
                                        error={this.state.errors.get('sendNotificationOrganizationInbox')}
                                        onChange={async (value) =>
                                            await this.handleChange('sendNotificationOrganizationInbox', value)
                                        }
                                    />
                                </InputGroup>
                            </FormGroup>
                            <FormGroup hasErrors={this.state.errors.has('emailAddressOrganizationInbox')}>
                                <FormLabel>{translateKey('serviceProvider.emailAddressOrganizationInbox')}</FormLabel>
                                <InputGroup>
                                    <NullableInputText
                                        error={this.state.errors.get('emailAddressOrganizationInbox')}
                                        onChange={async (value) =>
                                            await this.handleChange('emailAddressOrganizationInbox', value)
                                        }
                                        onDoValidate={async () =>
                                            await this.validateOne('emailAddressOrganizationInbox')
                                        }
                                        placeholder={translateKey('serviceProvider.emailAddressOrganizationInbox')}
                                        value={serviceOrganizationSettings.emailAddressOrganizationInbox}
                                    />
                                </InputGroup>
                            </FormGroup>
                            <FormGroup hasErrors={this.state.errors.has('emailAddressOrganizationInboxAlt')}>
                                <FormLabel>
                                    {translateKey('serviceProvider.emailAddressOrganizationInboxAlt')}
                                </FormLabel>
                                <InputGroup>
                                    <NullableInputText
                                        error={this.state.errors.get('emailAddressOrganizationInboxAlt')}
                                        onChange={async (value) =>
                                            await this.handleChange('emailAddressOrganizationInboxAlt', value)
                                        }
                                        onDoValidate={async () =>
                                            await this.validateOne('emailAddressOrganizationInboxAlt')
                                        }
                                        placeholder={translateKey('serviceProvider.emailAddressOrganizationInboxAlt')}
                                        value={serviceOrganizationSettings.emailAddressOrganizationInboxAlt}
                                    />
                                </InputGroup>
                            </FormGroup>
                            <FormMessage
                                text={`${translateKey('supervisors.checkIfEmptyOrganizationEmailField')} (${
                                    this.state.organizationEmail
                                        ? this.state.organizationEmail
                                        : translateKey('serviceProvider.emptyGenericOrganizationEmailField')
                                })`}
                            />
                            {this.props.router.params.id && (
                                <FundingSettingsList
                                    organizationId={organizationId}
                                    serviceId={this.state.serviceOrganizationSettings?.serviceId ?? serviceId}
                                    serviceOrganizationSettingId={this.props.router.params.id}
                                ></FundingSettingsList>
                            )}
                            {this.props.router.params.id && (
                                <CompensationPayoutList
                                    organizationId={organizationId}
                                    serviceCode={this.state.serviceCode}
                                    serviceId={serviceId}
                                    serviceOrganizationSettingId={this.props.router.params.id}
                                    serviceType={this.state.serviceType}
                                />
                            )}
                            {this.state.serviceGraderPools && this.state.serviceType === ServiceType.examination && (
                                <>
                                    <FormHeader>{translateKey('serviceGraderPool.serviceGraderPool')}</FormHeader>

                                    <FormGroup hasErrors={this.state.errors.has('serviceGraderPoolId')}>
                                        <FormLabel>{translateKey('serviceGraderPool.serviceGraderPool')}</FormLabel>
                                        <InputGroup>
                                            <Dropdown
                                                error={this.state.errors.get('serviceGraderPoolId')}
                                                onChange={async (value) =>
                                                    await this.handleChange('serviceGraderPoolId', value)
                                                }
                                                onDoValidate={async () => await this.validateOne('serviceGraderPoolId')}
                                                selectOptions={this.getServiceGraderPools()}
                                                value={serviceOrganizationSettings.serviceGraderPoolId}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                </>
                            )}
                            {this.state.serviceCode && getAnalystAllowedByServiceCode(this.state.serviceCode) && (
                                <>
                                    <FormHeader>{translateKey('serviceProvider.analyst')}</FormHeader>
                                    <FormGroup>
                                        <FormLabel>{translateKey('serviceProvider.analyst')}</FormLabel>
                                        <InputGroup>
                                            <InputCheckbox
                                                checked={this.state.useAnalyst}
                                                onChange={async (value) => {
                                                    await this.setState({ useAnalyst: value });
                                                    if (!value) {
                                                        await this.handleChange(
                                                            'ksyosHolterAnalystOrganizationId',
                                                            null
                                                        );
                                                    }
                                                }}
                                            />
                                        </InputGroup>
                                    </FormGroup>

                                    {this.state.useAnalyst && (
                                        <FormGroup
                                            hasErrors={this.state.errors.has('ksyosHolterAnalystOrganizationId')}
                                        >
                                            <FormLabel isRequired>
                                                {translateKey('organization.organization')}
                                            </FormLabel>
                                            <InputGroup>
                                                <OrganizationSelection
                                                    error={this.state.errors.get('ksyosHolterAnalystOrganizationId')}
                                                    onChange={async (changedOrganizationId) => {
                                                        await this.handleChange(
                                                            'ksyosHolterAnalystOrganizationId',
                                                            changedOrganizationId || null
                                                        );
                                                    }}
                                                    onDoValidate={async () => {
                                                        await this.validateOne('ksyosHolterAnalystOrganizationId');
                                                        this.validateKsyosHolterAnalystOrganizationId();
                                                    }}
                                                    showCity
                                                    value={
                                                        serviceOrganizationSettings.ksyosHolterAnalystOrganizationId ??
                                                        undefined
                                                    }
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    )}
                                </>
                            )}
                            {this.state.serviceCode === ServiceCode.GGZOembl && (
                                <>
                                    <FormHeader>{translateKey('embloomQuestionnaires.name')}</FormHeader>

                                    {!this.state.serviceOrganizationSettings?.serviceGroupSettingId &&
                                        translateKey('embloomQuestionnaires.noGroup')}
                                    {!this.hasCertificate() && translateKey('embloomQuestionnaires.noCertificate')}

                                    <Button
                                        color="secondary"
                                        onClick={async () => {
                                            const serviceOrganizationSettingsId = await this.save();
                                            if (serviceOrganizationSettingsId) {
                                                this.props.router.navigate(
                                                    `/questionnaireServiceOrganization/${serviceOrganizationSettingsId}?` +
                                                        `${organizationId ? '&organizationId=' + organizationId : ''}` +
                                                        `${serviceId ? '&serviceId=' + serviceId : ''}`
                                                );
                                            }
                                        }}
                                    >
                                        {translateKey('embloomQuestionnaires.manage')}
                                    </Button>
                                    <FormDivider />
                                </>
                            )}
                            <CancelButton
                                disabled={this.state.isSaving}
                                to={this.returnTo()}
                            />
                            <SaveSubmit
                                disabled={this.state.isSaving || !!this.state.overlap || this.state.isLoading}
                            />
                        </FormContainer>
                    )}
                />
            </FixedWidthTemplate>
        );
    }

    private async handleChange<K extends keyof IServiceOrganizationSettings>(
        name: K,
        value: IServiceOrganizationSettings[K]
    ) {
        await this.setState(({ serviceOrganizationSettings }) => ({
            serviceOrganizationSettings: { ...serviceOrganizationSettings, [name]: value },
        }));

        if (
            this.state.serviceOrganizationSettings &&
            (name === 'organizationId' || name === 'serviceId' || name === 'startDate' || name === 'endDate')
        ) {
            await this.atMostOncePerSecond.callMeMaybe(async () => {
                const overlap = await this.overlapChecker.check(
                    {
                        organizationId: this.state.serviceOrganizationSettings!.organizationId,
                        serviceId: this.state.serviceOrganizationSettings!.serviceId,
                    },
                    this.state.serviceOrganizationSettings!.startDate,
                    this.state.serviceOrganizationSettings!.endDate,
                    this.props.router.params.id
                );
                await this.setState({ overlap });
            });
        }
    }

    private async reloadOrganization(organizationId: string | null) {
        if (organizationId) {
            const result = await fetchJson(`/api/organizations/${organizationId}`, 'GET');
            await this.setState({ organizationEmail: result.json.emailAddressRequestInbox });
        } else {
            await this.setState({ organizationEmail: null });
        }
    }

    private async reloadServiceGraderPools(serviceId: string | null) {
        if (serviceId) {
            const serviceResult = await fetchJson(`/api/services/${serviceId}`, 'GET');
            if (serviceResult.json.serviceType !== ServiceType.examination) {
                await this.setState({
                    serviceType: serviceResult.json.serviceType,
                    serviceGraderPools: [],
                    serviceCode: serviceResult.json.code,
                });
            } else {
                const result = await fetchJson(`/api/serviceGraderPools?serviceId=${serviceId}`, 'GET');
                await this.setState({
                    serviceGraderPools: result.json.items,
                    serviceType: serviceResult.json.serviceType,
                    serviceCode: serviceResult.json.code,
                });
            }
        } else {
            await this.setState({ serviceType: null, serviceCode: null, serviceGraderPools: [] });
        }
    }

    private async validateOne(name: keyof IServiceOrganizationSettings) {
        await this.setState(({ serviceOrganizationSettings, errors }) => ({
            errors: updateValidationErrorsForOne(
                serviceOrganizationSettingAddEditSchema,
                serviceOrganizationSettings,
                errors,
                name
            ),
        }));
    }

    private async load() {
        const serviceId = this.props.router.searchParams.getOptional('serviceId');
        const organizationId = this.props.router.searchParams.getOptional('organizationId');
        await this.setState({ isLoading: true });
        if (this.props.router.params.id) {
            const result = await fetchJson(
                `/api/serviceOrganizationSettings/${this.props.router.params.id}?includeDeletedGroupHistory=true`,
                'GET'
            );
            const serviceGraderPoolsResult = await fetchJson(
                `/api/serviceGraderPools?serviceId=${result.json.serviceId}`,
                'GET'
            );
            const serviceResult = await fetchJson(`/api/services/${result.json.serviceId}`, 'GET');
            const organizationResult = await fetchJson(`/api/organizations/${result.json.organizationId}`, 'GET');

            await this.setState({
                serviceGraderPools: serviceGraderPoolsResult.json.items,
                serviceType: serviceResult.json.serviceType,
                serviceCode: serviceResult.json.code,
                serviceName: serviceId ? serviceResult.json.name : null,
                organizationName: organizationId ? organizationResult.json.name : null,
                organizationEmail: organizationResult.json.emailAddressRequestInbox,
                serviceOrganizationSettings: {
                    ...result.json,
                    recipientIsOwnOrganization: result.json.recipientIsOwnOrganization,
                    serviceOrganizationGroupHistory: result.json.serviceOrganizationGroupHistory.map(
                        (item: {
                            endDate: string | null;
                            lastUpdatedByPractitioner: IServiceOrganizationGroupHistoryPractitioner | null;
                            lastUpdatedByPractitionerId: string;
                            startDate: string;
                        }) => ({
                            ...item,
                            startDate: DateTime.from(item.startDate),
                            endDate: item.endDate ? DateTime.from(item.endDate) : null,
                            lastUpdatedByPractitioner: item.lastUpdatedByPractitionerId
                                ? item.lastUpdatedByPractitioner
                                : null,
                            isNew: false,
                        })
                    ),
                },
                useAnalyst: result.json.ksyosHolterAnalystOrganizationId !== null,
            });

            await this.checkServiceProviderIsRequestRecipient();
        } else if (serviceId) {
            const serviceResult = await fetchJson('/api/services/' + serviceId, 'GET');
            const serviceGraderPools =
                serviceResult.json.serviceType === ServiceType.examination
                    ? (await fetchJson(`/api/serviceGraderPools?serviceId=${serviceId}`, 'GET')).json.items
                    : [];
            await this.setState({
                serviceType: serviceResult.json.serviceType,
                serviceCode: serviceResult.json.code,
                serviceName: serviceResult.json.name,
                organizationName: null,
                serviceGraderPools: serviceGraderPools,
                serviceOrganizationSettings: {
                    serviceId: serviceId,
                    startDate: DateTime.now().format('YYYY-MM-DD'),
                    endDate: null,
                    somnologyOrganizationType: null,
                    sendBittiumRecordingsTo: null,
                    optoConnectPracticeId: null,
                    isRequestRecipient: true,
                    recipientIsOwnOrganization: false,
                    organizationId: null,
                    serviceGraderPoolId: null,
                    sendNotificationOrganizationInbox: false,
                    emailAddressOrganizationInbox: null,
                    emailAddressOrganizationInboxAlt: null,
                    ksyosHolterAnalystOrganizationId: null,
                    receiveZorgmailOnConsultOpen: false,
                    zorgmailNummer: null,
                    serviceGroupSettingId: null,
                    canReferPatientWithoutSmartphone: false,
                    isSendingDeviceToPatientsAddress: false,
                    isUsingHideRequestedExaminationTaskFeature: false,
                    serviceOrganizationGroupHistory: [],
                },
            });
        } else if (organizationId) {
            const result = await fetchJson(`/api/organizations/${organizationId}`, 'GET');
            const servicesResult = await fetchJson('/api/services?selectionList=true&serviceIsActive=true', 'GET');
            await this.setState({
                organizationName: result.json.name,
                serviceName: null,
                serviceCode: null,
                organizationEmail: result.json.emailAddressRequestInbox,
                services: servicesResult.json.items,
                serviceOrganizationSettings: {
                    organizationId: organizationId,
                    serviceId: null,
                    startDate: DateTime.now().format('YYYY-MM-DD'),
                    endDate: null,
                    somnologyOrganizationType: null,
                    sendBittiumRecordingsTo: null,
                    optoConnectPracticeId: null,
                    isRequestRecipient: true,
                    recipientIsOwnOrganization: false,
                    serviceGraderPoolId: null,
                    sendNotificationOrganizationInbox: false,
                    emailAddressOrganizationInbox: null,
                    emailAddressOrganizationInboxAlt: null,
                    ksyosHolterAnalystOrganizationId: null,
                    receiveZorgmailOnConsultOpen: false,
                    zorgmailNummer: null,
                    serviceGroupSettingId: null,
                    canReferPatientWithoutSmartphone: false,
                    isSendingDeviceToPatientsAddress: false,
                    isUsingHideRequestedExaminationTaskFeature: false,
                    serviceOrganizationGroupHistory: [],
                },
            });
        }
        await this.loadServiceConsumersAndServiceProvidersForOSASV();
        await this.loadServiceGroupSettings();
        await this.setState({ isLoading: false });
    }

    private async checkServiceProviderIsRequestRecipient() {
        if (
            this.state.serviceOrganizationSettings &&
            this.state.serviceOrganizationSettings.serviceId &&
            this.state.serviceOrganizationSettings.organizationId &&
            !this.state.serviceOrganizationSettings.isRequestRecipient &&
            !this.state.serviceOrganizationSettings.recipientIsOwnOrganization
        ) {
            const url = `/api/serviceProviders?serviceId=${this.state.serviceOrganizationSettings.serviceId}&organizationId=${this.state.serviceOrganizationSettings.organizationId}&isActive=true`;
            const result = await fetchJson(url, 'GET');
            for (const serviceProvider of result.json.items) {
                if (serviceProvider.isRequestRecipient) {
                    return await this.setState({ messageNoServiceProviderRecipient: false });
                }
            }

            return await this.setState({ messageNoServiceProviderRecipient: true });
        } else if (this.state.messageNoServiceProviderRecipient) {
            await this.setState({ messageNoServiceProviderRecipient: false });
        }
    }

    private async loadServiceConsumersAndServiceProvidersForOSASV() {
        if (
            this.state.serviceCode === ServiceCode.OSASV &&
            this.state.serviceOrganizationSettings &&
            this.state.serviceOrganizationSettings.serviceId &&
            this.state.serviceOrganizationSettings.organizationId
        ) {
            await Promise.all([
                this.loadServiceConsumers(
                    this.state.serviceOrganizationSettings.serviceId,
                    this.state.serviceOrganizationSettings.organizationId
                ),
                this.loadServiceProviders(
                    this.state.serviceOrganizationSettings.serviceId,
                    this.state.serviceOrganizationSettings.organizationId
                ),
            ]);
        }
    }

    private async loadServiceProviders(serviceId: string, organizationId: string) {
        const url = `/api/serviceProviders?serviceId=${serviceId}&isActive=${true}&organizationId=${organizationId}&includeServiceGroups=true`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ serviceProviders: result.json.items as IServiceProvider[] });
    }

    private async loadServiceConsumers(serviceId: string, organizationId: string) {
        const url = `/api/serviceConsumers?serviceId=${serviceId}&isActive=${true}&organizationId=${organizationId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ serviceConsumers: result.json.items as IServiceConsumer[] });
    }

    private getServicesOptions() {
        return [{ value: '', text: translateKey('common.selectItem', translateKey('service.service')) }].concat(
            this.state.services.map((e) => ({ value: e.id, text: e.name }))
        );
    }

    private getServiceGraderPools() {
        return [
            {
                value: '',
                text: translateKey('common.selectItem', translateKey('serviceGraderPool.selectServiceGraderPool')),
            },
        ].concat(this.state.serviceGraderPools.map((e) => ({ value: e.id, text: e.name })));
    }

    private async save() {
        let url = '/api/serviceOrganizationSettings';

        if (this.props.router.params.id) {
            url += '/' + this.props.router.params.id;
        }

        await this.validateSomnologyOrganization();
        await this.validateKsyosHolterAnalystOrganizationId();

        this.serviceOrganizationGroupHistorySelectorValidation(
            validateServiceOrganizationGroupHistorySelector(
                this.state.serviceOrganizationSettings?.serviceOrganizationGroupHistory ?? []
            )
        );

        if (
            this.state.errors.filter(
                (_, key) =>
                    !(
                        key?.startsWith('serviceOrganizationGroupHistory.') &&
                        (key?.endsWith('.startDate') || key?.endsWith('.endDate'))
                    )
            ).size > 0
        ) {
            scrollToValidationErrorByClassName('form-container');

            return;
        }

        if (this.state.errors.size > 0) {
            scrollToFormControlFeedback();

            return;
        }

        const { errors, item } = validate(serviceOrganizationSettingAddEditSchema, {
            ...this.state.serviceOrganizationSettings,
            serviceOrganizationGroupHistory:
                this.state.serviceOrganizationSettings?.serviceOrganizationGroupHistory.map((entry) => ({
                    ...entry,
                    startDate: entry.startDate?.format('YYYY-MM-DD') ?? null,
                    endDate: entry.endDate?.format('YYYY-MM-DD') ?? null,
                })) ?? [],
        });

        await this.setState({ errors });

        if (
            errors.filter(
                (_, key) =>
                    !(
                        key?.startsWith('serviceOrganizationGroupHistory.') &&
                        (key?.endsWith('.startDate') || key?.endsWith('.endDate'))
                    )
            ).size > 0
        ) {
            scrollToValidationErrorByClassName('form-container');

            return;
        }

        if (errors.size > 0) {
            scrollToValidationErrorByClassName('form-container');

            return;
        }

        await this.setState({ isSaving: true });

        return (await fetchJson(url, 'POST', item)).json.id;
    }

    private returnTo() {
        const serviceId = this.props.router.searchParams.get('serviceId');
        if (serviceId) {
            return `/services/${serviceId}?tab=providerOrganizationSettings`;
        }

        return `/organizations/${this.props.router.searchParams.get('organizationId')}?tab=serviceOrganizationSettings`;
    }

    private async validateSomnologyOrganization() {
        const somnologyOrganizationTypeRequired =
            this.state.serviceCode &&
            [ServiceCode.OSASO, ServiceCode.OSASVO, ServiceCode.OSASV].includes(this.state.serviceCode) &&
            this.state.serviceOrganizationSettings &&
            this.state.serviceOrganizationSettings.somnologyOrganizationType === null;

        if (somnologyOrganizationTypeRequired) {
            await this.setState(({ errors }) => ({ errors: errors.set('somnologyOrganizationType', 'any_required') }));
        } else {
            await this.setState(({ errors }) => ({ errors: errors.remove('somnologyOrganizationType') }));
        }
    }

    private async validateKsyosHolterAnalystOrganizationId() {
        if (
            this.state.useAnalyst &&
            this.state.serviceOrganizationSettings &&
            this.state.serviceOrganizationSettings.ksyosHolterAnalystOrganizationId === null
        ) {
            await this.setState(({ errors }) => ({
                errors: errors.set('ksyosHolterAnalystOrganizationId', 'any_required'),
            }));
        } else {
            await this.setState(({ errors }) => ({ errors: errors.remove('ksyosHolterAnalystOrganizationId') }));
        }
    }

    private async loadServiceGroupSettings() {
        const serviceId = this.state.serviceOrganizationSettings?.serviceId;
        if (serviceId) {
            const url = `/api/serviceGroupSettings?serviceId=${serviceId}&isActive=true`;
            const results = await fetchJson(url, 'GET');
            await this.setState({ serviceGroupSettings: results.json.items });
        } else {
            await this.setState({ serviceGroupSettings: [] });
        }
    }

    private async hasTcohBiometricianInOrganization() {
        const organizationId =
            this.props.router.searchParams.get('organizationId') ||
            this.state.serviceOrganizationSettings?.organizationId;
        if (organizationId) {
            const serviceProvidersTcohBiometricianInOrganization = (
                await fetchJson(
                    `/api/serviceProviders?serviceCode=${ServiceCode.TCOH}&organizationId=${organizationId}&isBiometrist=true&isActive=true`,
                    'GET'
                )
            ).json;
            if (serviceProvidersTcohBiometricianInOrganization.total > 0) {
                return this.setState({ hasTcohBiometricianInOrganization: true });
            }
        }
    }

    private hasCertificate() {
        const groupSettings =
            this.state.serviceOrganizationSettings?.serviceGroupSettingId &&
            this.state.serviceGroupSettings.find(
                (item) => item.id === this.state.serviceOrganizationSettings?.serviceGroupSettingId
            );
        if (groupSettings && groupSettings.embloomGroupCertificate) {
            return true;
        } else {
            return false;
        }
    }

    private getActiveGroupName() {
        return (
            this.state.serviceOrganizationSettings?.serviceOrganizationGroupHistory
                .filter((item) => !item.isDeleted)
                .find(
                    (serviceOrganizationGroup) =>
                        serviceOrganizationGroup.startDate && isActiveOnDate(serviceOrganizationGroup, DateTime.now())
                )?.serviceGroupSetting?.group.name ?? '-'
        );
    }

    private async serviceOrganizationGroupHistorySelectorValidation(
        serviceOrganizationGroupHistoriesErrors: Map<string, string | null>
    ) {
        return await this.setState(({ errors }) => {
            errors
                .filter((_, key) => !!key?.startsWith('serviceOrganizationGroupHistory.'))
                .forEach((_, key) => {
                    if (key) {
                        errors = errors.remove(key);
                    }
                });

            for (const [key, value] of serviceOrganizationGroupHistoriesErrors) {
                if (value) {
                    errors = errors.set(`serviceOrganizationGroupHistory.${key}`, value);
                }
            }

            return { errors };
        });
    }
}

export const ServiceOrganizationSettingsAddEdit = withRouter(InnerServiceOrganizationSettingsAddEdit);
